<script setup>
import { ref, watch } from "vue";
import { useField } from "vee-validate";
import * as yup from "yup";

const props = defineProps({
  formName: String,
  name: String,
  label: String,
  initialValue: String,
  dbFieldName: String,
  placeholder: String,
  disabled: Boolean,
  checked: Boolean,
  class: String,
  schema: String,
  trueValue: {
    type: String,
    default: "1",
  },
  falseValue: {
    type: String,
    default: "0",
  },
  modelValue: {
    type: [Array, Object, String],
    default: () => [],
  },
  type: {
    type: String,
    default: "boolean",
  },
  required: {
    type: Boolean,
    default: true,
  },
});

const field = props.dbFieldName || props.name;

let schema = yup.mixed().oneOf([props.trueValue, props.falseValue]);

if (props.schema) {
  schema = props.schema;
} else {
  if (props.required) {
    schema = schema.required(`${props.label} is required.`);
  }
}

const { value, errorMessage } = useField(props.name, schema, {
  initialValue:
    props.modelValue?.length || Object.keys(props.modelValue)?.length
      ? props.modelValue
      : "",
});

const emit = defineEmits(["update:modelValue"]);

watch(value, (newValue) => {
  let updatedValue;
  if (Array.isArray(props.modelValue)) {
    if (newValue === props.trueValue) {
      // Use Set to prevent duplicate entries
      updatedValue = [...new Set([...props.modelValue, props.trueValue])];
    } else {
      updatedValue = props.modelValue.filter(
        (item) => item !== props.trueValue
      );
    }
  } else {
    updatedValue = newValue ? props.trueValue : props.falseValue;
  }

  emit("update:modelValue", updatedValue);
});
</script>

<template>
  <fieldset class="frm-field" :class="props.class">
    <label
      :for="name"
      class="flex items-center gap-3 mr-40"
      :class="{ 'pointer-events-none': disabled }">
      <input
        type="checkbox"
        :name="name"
        :checked="modelValue.includes(trueValue) || checked"
        :disabled="disabled"
        @change="
          (event) =>
            emit(
              'update:modelValue',
              event.target.checked
                ? [...new Set([...modelValue, trueValue])]
                : modelValue.filter((item) => item !== trueValue)
            )
        "
        class="self-start rounded shrink-0"
        :class="{ 'opacity-75 grayscale': disabled }" />
      <span v-html="label"></span>
    </label>
    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none">
      {{ errorMessage }}
    </span>
  </fieldset>
</template>
